import { Controller } from "@hotwired/stimulus"
import { GridStack } from "gridstack"

export default class extends Controller {
    connect() {

        const layout = this.safeJSON(this.element.dataset.layout)
        const mainEl = this.element.querySelector("#main-grid")
        this.visibility = layout.visibility || {}
        if (!mainEl) return

        // Grille principale
        this.mainGrid = GridStack.init({
            float: true,
            margin: 10,
            cellHeight: 280,
            animate: true,
            subGridDynamic: false,
            disableOneColumnMode: true,
            acceptWidgets: el => this.canAccept(el, true),
            removable: false,
            dragOut: true,
            draggable: { handle: ".card-header" },
            resizable: { handles: "e, se, s, sw, w" },
            swap: true
        }, mainEl)

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {

                this.mainGrid.setAnimation(true)

                const el = mainEl.closest(".app-content-dashboard-charts")
                el?.classList.remove("pre-grid")

                window.dispatchEvent(new Event("resize"))
            })
        })
        setTimeout(() => {
            this.applyWidgetVisibility(this.visibility);
            this.rebuildVisibilityList();
        }, 100);
        this.bindChangeEvents(this.mainGrid)
        this.mainGrid.on("added removed change", () => {
            setTimeout(() => this.applyWidgetVisibility(this.visibility), 50);
            setTimeout(() => this.rebuildVisibilityList(), 50); // ⬅️ important aussi ici
        });
        this.bindSubGridActions()
        this.bindDropHandling(this.mainGrid)
        this.bindHoverEffects()
        this.bindRenameActions()

        // désactiver drag & resize par défaut
        this.dragEnabled = false
        this.mainGrid.enableMove(false)
        this.mainGrid.enableResize(false)
        document.querySelectorAll(".grid-stack-item-content").forEach(el => el.classList.add("locked"))

        const addBtn = document.getElementById("add-subgrid")
        const restoreBtn = document.getElementById("restore-layout")
        const toggleBtn = document.getElementById("toggle-drag")

        if (addBtn) addBtn.disabled = true
        if (restoreBtn) restoreBtn.disabled = true

        if (addBtn) {
            addBtn.addEventListener("click", () => {
                this.addSubGrid(this.mainGrid)
                this.saveLayout()
            })
        }

        if (restoreBtn) {
            restoreBtn.addEventListener("click", () => {
                const confirmMsg =
                    "Êtes-vous sûr de vouloir restaurer la disposition par défaut ?\nToutes vos modifications seront perdues."
                if (window.confirm(confirmMsg)) this.restoreDefaultLayout()
            })
        }

        if (toggleBtn) {
            const tooltip = new bootstrap.Tooltip(toggleBtn)

            toggleBtn.addEventListener("click", () => {
                this.dragEnabled = !this.dragEnabled

                this.mainGrid.enableMove(this.dragEnabled)
                this.mainGrid.enableResize(this.dragEnabled)

                document.querySelectorAll(".grid-stack-item-content").forEach(el => {
                    el.classList.toggle("locked", !this.dragEnabled)
                })

                if (addBtn) addBtn.disabled = !this.dragEnabled
                if (restoreBtn) restoreBtn.disabled = !this.dragEnabled

                const iconWrapper = toggleBtn.querySelector(".fa-wrapper-12")
                const iconHTML = this.dragEnabled
                    ? '<i class="fa-solid fa-unlock"></i>'
                    : '<i class="fa-solid fa-lock"></i>'

                if (iconWrapper) {
                    iconWrapper.innerHTML = iconHTML
                }

                if (this.dragEnabled) {
                    toggleBtn.classList.replace("btn-outline-secondary", "btn-outline-success")
                    toggleBtn.setAttribute("data-bs-original-title", "Verrouiller la mise en page")
                    document.body.classList.add("dashboard-edit-mode")
                } else {
                    toggleBtn.classList.replace("btn-outline-success", "btn-outline-secondary")
                    toggleBtn.setAttribute("data-bs-original-title", "Déverrouiller la mise en page")
                    document.body.classList.remove("dashboard-edit-mode")
                }

                const existingTooltip = bootstrap.Tooltip.getInstance(toggleBtn)
                if (existingTooltip) existingTooltip.dispose()
                new bootstrap.Tooltip(toggleBtn)
            })
        }

        const visibilitySaveBtn = document.getElementById("saveWidgetVisibility")
        if (visibilitySaveBtn) {
            visibilitySaveBtn.addEventListener("click", () => this.saveWidgetVisibility())
        }
    }

    // Validation drop
    canAccept(el, isMain = false) {
        if (!el) return true
        const isSubgrid = el.classList.contains("subgrid-wrapper") || el.id?.startsWith("subgrid_")
        if (isMain) return true
        if (isSubgrid) return false
        return true
    }

    // === Restauration ===
    restoreLayout(grid, layout) {
        if (!layout?.children) return
        layout.children.forEach(node => {
            if (node.subGridOpts) {
                const subGrid = this.addSubGrid(grid, node)
                if (node.subGridOpts?.children?.length) {
                    requestAnimationFrame(() => {
                        node.subGridOpts.children.forEach(child => {
                            const el = document.getElementById(child.id)
                            if (el) {
                                this.mainGrid.removeWidget(el, false, false)
                                subGrid.el.appendChild(el)
                                subGrid.makeWidget(el)
                                subGrid.update(el, { x: child.x, y: child.y, w: child.w, h: child.h })
                            }
                        })
                    })
                }
                if (node.title) {
                    const titleEl = subGrid.el.closest(".subgrid-wrapper")?.querySelector("h6")
                    if (titleEl) titleEl.textContent = node.title
                }
            } else {
                const el = document.getElementById(node.id)
                if (el) {
                    grid.makeWidget(el)
                    grid.update(el, { x: node.x, y: node.y, w: node.w, h: node.h })
                }
            }
        })
    }

    // Ajout section
    addSubGrid(parentGrid, node = null) {
        const id = node?.id || `subgrid_${Date.now()}`
        const wrapper = document.createElement("div")
        wrapper.classList.add("grid-stack-item")
        wrapper.id = id

        wrapper.innerHTML = `
      <div class="grid-stack-item-content card border-0 shadow-sm subgrid-wrapper">
        <div class="card-header d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center gap-2">
            <div class="fa-wrapper-12">
              <i class="fas fa-layer-group"></i>
            </div>
            <h6 class="mb-0 subgrid-title" title="Double-cliquez ou cliquez sur le crayon pour renommer">
              ${node?.title || "Section"}
            </h6>
            <i class="fa-solid fa-pen-to-square edit-hint ms-1"
               title="Renommer la section"
               style="cursor:pointer;"></i>
          </div>
          <div class="d-flex align-items-center ms-auto">
            <button
              type="button"
              class="btn btn-sm btn-light border text-danger btn-icon-square remove-subgrid"
              title="Supprimer la section"
            >
              <i class="fas fa-times text-danger"></i>
            </button>
          </div>
        </div>
        <div class="card-body p-2">
          <div class="grid-stack sub-grid drop-zone" id="${id}_grid"
               style="min-height:300px;border-radius:6px;transition:all 0.2s ease;"></div>
        </div>
      </div>`

        parentGrid.el.appendChild(wrapper)

        const position = parentGrid.getRow()
        parentGrid.makeWidget(wrapper)
        parentGrid.update(wrapper, node || { x: 0, y: position, w: 6, h: 4 })
        parentGrid.compact()

        // Section interne
        const subEl = wrapper.querySelector(".sub-grid")
        const subGrid = GridStack.addGrid(subEl, {
            float: true,
            margin: 10,
            cellHeight: 130,
            animate: true,
            subGridDynamic: false,
            disableOneColumnMode: true,
            acceptWidgets: el => this.canAccept(el, false),
            removable: false,
            dragOut: false,
            draggable: { handle: ".card-header" },
            resizable: { handles: "e, se, s, sw, w" },
            swap: true
        })

        this.bindChangeEvents(subGrid)
        this.bindDropHandling(subGrid)

        // setTimeout(() => {
        //     wrapper.scrollIntoView({ behavior: "smooth", block: "center" })
        //     wrapper.classList.add("highlighted-subgrid")
        //     setTimeout(() => wrapper.classList.remove("highlighted-subgrid"), 1000)
        // }, 250)

        if (!node) {
            setTimeout(() => {
                wrapper.scrollIntoView({ behavior: "smooth", block: "center" })
                wrapper.classList.add("highlighted-subgrid")
                setTimeout(() => wrapper.classList.remove("highlighted-subgrid"), 1000)
            }, 250)
        }

        setTimeout(() => this.applyWidgetVisibility(this.visibility), 50)
        setTimeout(() => this.rebuildVisibilityList(), 50);

        return subGrid
    }

    //Suppression d'une section
    bindSubGridActions() {
        document.addEventListener("click", e => {
            const btn = e.target.closest(".remove-subgrid")
            if (!btn) return

            const wrapper = btn.closest(".grid-stack-item")
            const subGridEl = wrapper.querySelector(".sub-grid")

            const subGrid = subGridEl?.gridstack
            const hasChildren = subGrid && subGrid.engine.nodes.length > 0

            if (hasChildren) {
                e.preventDefault()
                e.stopPropagation()
                alert("Impossible de supprimer cette section : elle contient encore des widgets.")
                return
            }

            const confirmMsg = btn.dataset.confirm || "Supprimer cette section ?"
            if (!window.confirm(confirmMsg)) return

            const grid = wrapper.closest(".grid-stack").gridstack
            grid.removeWidget(wrapper)
            this.saveLayout()
            this.rebuildVisibilityList();

            wrapper.classList.add("fade-out")
            setTimeout(() => wrapper.remove(), 300)
        })
    }

    //Restauration du layout par défaut
    restoreDefaultLayout() {
        fetch("/backoffice/dashboard/restore_default", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
            }
        }).then(() => {
            window.location.reload()
        })
    }

    //Sauvegarde
    saveLayout() {
        if (!this.mainGrid) return
        const extract = grid => {
            const children = grid.engine.nodes.map(node => {
                const id = node.el?.id
                if (!id) return null
                const base = { id, x: node.x, y: node.y, w: node.w, h: node.h }
                if (node.subGrid) {
                    base.subGridOpts = extract(node.subGrid)
                    const titleEl = node.el.querySelector(".subgrid-title")
                    if (titleEl) base.title = titleEl.textContent.trim()
                }
                return base
            }).filter(Boolean)
            return { children }
        }

        const layout = extract(this.mainGrid)
        layout.visibility = this.visibility || {}
        console.log(" Sauvegarde layout :", layout)

        fetch("/backoffice/dashboard/save_layout", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
            },
            body: JSON.stringify({ layout })
        })
    }

    bindDropHandling(grid) {
        grid.on("dropped", () => this.saveLayout())
    }

    bindChangeEvents(grid) {
        let initialized = false
        const save = () => {
            if (!initialized) return
            setTimeout(() => this.saveLayout(), 250)
        }

        setTimeout(() => initialized = true, 500)

        grid.on("change", save)
        grid.on("added", save)
        grid.on("removed", save)
    }

    bindRenameActions() {
        const startEditing = title => {
            const oldValue = title.textContent.trim()
            const input = document.createElement("input")
            input.type = "text"
            input.value = oldValue
            input.className = "form-control form-control-sm fw-bold"
            input.style.width = "75%"
            title.replaceWith(input)
            input.focus()

            const saveChange = () => {
                const newValue = input.value.trim() || "Section"
                const newTitle = document.createElement("h6")
                newTitle.className = "mb-0 subgrid-title"
                newTitle.textContent = newValue
                input.replaceWith(newTitle)
                this.saveLayout()
                this.rebuildVisibilityList();
            }

            input.addEventListener("blur", saveChange)
            input.addEventListener("keydown", ev => {
                if (ev.key === "Enter") {
                    ev.preventDefault()
                    saveChange()
                }
            })
        }

        document.addEventListener("dblclick", e => {
            const title = e.target.closest(".subgrid-title")
            if (title) startEditing(title)
        })

        document.addEventListener("click", e => {
            const icon = e.target.closest(".edit-hint")
            if (icon) {
                const title = icon.parentElement.querySelector(".subgrid-title")
                if (title) startEditing(title)
            }
        })
    }

    bindHoverEffects() {
        document.addEventListener("dragenter", e => {
            const zone = e.target.closest(".drop-zone")
            if (zone) zone.classList.add("drop-hover")
        })
        document.addEventListener("dragleave", e => {
            const zone = e.target.closest(".drop-zone")
            if (zone) zone.classList.remove("drop-hover")
        })
        document.addEventListener("drop", () => {
            document.querySelectorAll(".drop-zone.drop-hover").forEach(el => el.classList.remove("drop-hover"))
        })
    }
    saveWidgetVisibility() {
        const toggles = document.querySelectorAll(".widget-toggle");
        const visibility = {};

        toggles.forEach(cb => {
            visibility[cb.dataset.widgetId] = cb.checked;
        });

        this.visibility = visibility;
        const extract = grid => {
            return {
                children: grid.engine.nodes.map(node => {
                    const id = node.el?.id;
                    if (!id) return null;

                    const base = {id,x: node.x,y: node.y,w: node.w, h: node.h};

                    if (node.subGrid) {
                        base.title =
                            node.el.querySelector(".subgrid-title")?.textContent.trim() ||
                            "Section";
                        base.subGridOpts = extract(node.subGrid);
                    }

                    return base;
                }).filter(Boolean)
            };
        };
        const layout = extract(this.mainGrid);
        layout.visibility = visibility;

        fetch("/backoffice/dashboard/save_layout", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
            },
            body: JSON.stringify({ layout })
        }).then(() => {
            window.location.reload();
        });
    }

    applyWidgetVisibility(visibility) {
        if (!visibility) return;

        const items = document.querySelectorAll(".grid-stack-item");

        items.forEach(item => {
            const id = item.id;
            const visible = visibility[id] !== false;
            const isSubgrid = id.startsWith("subgrid_");

            if (isSubgrid) {
                item.style.display = visible ? "" : "none";
                return;
            }

            if (visible) {
                item.style.display = "";

                if (item.dataset.hidden === "1") {
                    this.mainGrid.makeWidget(item);
                    delete item.dataset.hidden;
                }

            } else {
                item.style.display = "none";

                if (!item.dataset.hidden) {
                    this.mainGrid.removeWidget(item, false, false);
                    item.dataset.hidden = "1";
                }
            }
        });

        this.mainGrid.compact();
    }

    buildTreeFromGrid(grid, depth = 0, list = []) {
        grid.engine.nodes.forEach(node => {
            const el = node.el;
            const id = el.id;

            const name =
                el.dataset.name ||
                el.querySelector(".subgrid-title")?.textContent.trim() ||
                "Section";

            list.push({
                id,
                name,
                depth,
                isSection: !!node.subGrid,
            });

            if (node.subGrid) {
                this.buildTreeFromGrid(node.subGrid, depth + 1, list);
            }
        });

        return list;
    }
    rebuildVisibilityList() {
        const container = document.querySelector("#widgetVisibilityList");
        if (!container) return;

        container.innerHTML = "";

        const tree = this.buildTreeFromGrid(this.mainGrid);

        tree.forEach(node => {
            const li = document.createElement("li");
            li.classList.add("list-group-item", "d-flex", "justify-content-between", "align-items-center");

            const padding = node.depth * 20;

            const icon = node.isSection
                ? '<i class="fa-solid fa-folder-open text-primary me-1"></i>'
                : '<i class="fa-solid fa-circle-dot text-secondary me-1"></i>';

            li.innerHTML = `
            <span style="padding-left:${padding}px; font-weight:${node.isSection ? "600" : "400"};">
                ${icon} ${node.name}
            </span>

            <input type="checkbox"
                   class="form-check-input widget-toggle"
                   data-widget-id="${node.id}"
                   ${this.visibility[node.id] !== false ? "checked" : ""}>
        `;

            container.appendChild(li);
        });

    }

    safeJSON(str) {
        try { return JSON.parse(str || "{}") } catch { return {} }
    }
}
